import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { incrementCurrentSlide, incrementPeekSlide } from "./slides";

export const submitAssessment = createAsyncThunk(
  "assessment/submitAssessment",
  async (payload, { getState }) => {
    const assessment = getState().assessment.data;
    const { user } = payload;

    try {
      const res = await axios.post("/RetailAssessment2", {
        assessmentCompleted: new Date(),
        authId: user?.sub || null,
        assessmentData: { questions: assessment.questions },
        note: assessment.note,
        patient: assessment.patient,
      });

      const results = JSON.parse(res?.data?.body);

      results.sort(
        (a, b) =>
          new Date(a.assessmentCompleted) - new Date(b.assessmentCompleted)
      );

      return results;
    } catch (error) {
      console.error("Error submitting assessment:", error);
      throw error;
    }
  }
);

export const updateAssessment = createAsyncThunk(
  "assessment/updateAssessment",
  async (payload) => {
    try {
      const { data } = await axios.post("/RetailAssessmentUpdate", payload);
      data.sort(
        (a, b) =>
          new Date(a.assessmentCompleted) - new Date(b.assessmentCompleted)
      );
      return data;
    } catch (error) {
      console.error("Error while updating assessment:", error);
      throw error;
    }
  }
);

export const fetchResults = createAsyncThunk(
  "assessment/fetchResults",
  async (payload) => {
    const { user } = payload;

    try {
      const { data } = await axios.get(
        window.encodeURI(`/RetailResults?authId=${user.sub}`)
      );

      data.sort(
        (a, b) =>
          new Date(a.assessmentCompleted) - new Date(b.assessmentCompleted)
      );

      return data;
    } catch (error) {
      console.error("Error fetching results:", error);
      throw error;
    }
  }
);

function processResults(results) {
  // Create an object to store the assessments for each day
  const assessmentsByDay = {};

  // Group assessments by day
  results.forEach((assessment) => {
    const assessmentDate = new Date(assessment.assessmentCompleted);
    const day = assessmentDate.toISOString().split("T")[0];

    if (!assessmentsByDay[day]) {
      assessmentsByDay[day] = [];
    }

    assessmentsByDay[day].push(assessment);
  });

  // Modify assessments to add the assessmentDateString property
  Object.values(assessmentsByDay).forEach((assessments) => {
    if (assessments.length > 1) {
      assessments.forEach((assessment) => {
        const assessmentDate = new Date(assessment.assessmentCompleted);
        assessment.assessmentDateString = assessmentDate
          .toLocaleString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            hour12: true,
          })
          .replace(" PM", "pm")
          .replace(" AM", "am")
          .replace(",", " ");
      });
    } else {
      const assessment = assessments[0];
      const assessmentDate = new Date(assessment.assessmentCompleted);
      assessment.assessmentDateString = assessmentDate
        .toLocaleString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour12: true,
        })
        .replace(",", " ");
    }
  });

  console.log(results);

  return results;
}

const initialState = {
  data: { questions: [], note: "", patient: null },
  isSubmitting: false,
  results: [],
};
export const assessmentSlice = createSlice({
  name: "assessment",
  initialState,
  reducers: {
    setAssessment: (state, action) => {
      state.data = action.payload;
    },
    setResults: (state, action) => {
      state.results = processResults(action.payload);
    },
    setPatient: (state, action) => {
      state.data.patient = action.payload;
    },
    setNote: (state, action) => {
      state.data.note = action.payload;
    },
    updateQuestion: (state, action) => {
      const index =
        state.data.questions?.findIndex(
          (c) => c.title === action.payload.title
        ) ?? -1;
      if (index === -1) {
        state.data.questions.push(action.payload);
      } else {
        state.data.questions[index].id = action.payload.id;
        state.data.questions[index].value = action.payload.value;
        state.data.questions[index].imageFrame = action.payload.imageFrame;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitAssessment.pending, (state) => {
        state.isSubmitting = true;
      })
      .addCase(submitAssessment.fulfilled, (state, action) => {
        state.results = processResults(action.payload);
        state.isSubmitting = false;
      })
      .addCase(submitAssessment.rejected, (state) => {
        state.isSubmitting = false;
      })
      .addCase(fetchResults.pending, (state) => {
        // Handle pending state for fetchResults
      })
      .addCase(fetchResults.fulfilled, (state, action) => {
        state.results = processResults(action.payload);
      })
      .addCase(updateAssessment.fulfilled, (state, action) => {
        state.results = processResults(action.payload);
      })
      .addCase(fetchResults.rejected, (state) => {
        // Handle rejected state for fetchResults
      });
  },
});

export const {
  setAssessment,
  setResults,
  setPatient,
  setNote,
  updateQuestion,
} = assessmentSlice.actions;

export default assessmentSlice.reducer;
